import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { DatePicker, DateTimePicker } from '@progress/kendo-react-dateinputs';

interface FormDatePickerProps extends FieldRenderProps {
  includeTime?: boolean;
  isLoading?: boolean;
}

const isEmptyDateValue = (dateFieldValue: unknown): boolean => {
  return dateFieldValue === null || dateFieldValue === undefined || dateFieldValue === '';
};

const toPickerDateValue = (dateFieldValue: unknown): Date | null => {
  if (isEmptyDateValue(dateFieldValue)) {
    return null;
  }
  if (dateFieldValue instanceof Date) {
    return dateFieldValue;
  }
  if (typeof dateFieldValue === 'string' || typeof dateFieldValue === 'number') {
    return new Date(dateFieldValue);
  }
  return null;
};

const useDatePickerResetKey = (value: unknown): number => {
  const [resetKey, setResetKey] = React.useState(0);
  const [previousValue, setPreviousValue] = React.useState(value);

  if (value !== previousValue) {
    setPreviousValue(value);
    if (isEmptyDateValue(value) && !isEmptyDateValue(previousValue)) {
      setResetKey(currentKey => currentKey + 1);
    }
  }

  return resetKey;
};

export const FormDatePicker: React.FC<FormDatePickerProps> = fieldRenderProps => {
  const { includeTime, name, value, onChange, disabled, isLoading } = fieldRenderProps;
  const dateValue = toPickerDateValue(value);
  const pickerKey = useDatePickerResetKey(value);

  const popupSettings = {
    popupClass: 'form-date-picker-popup',
    collision: {
      horizontal: 'flip' as const,
      vertical: 'flip' as const
    },
    animate: true
  };

  return (
    <FormFieldWrapper
      validationMessage={fieldRenderProps.validationMessage}
      value={fieldRenderProps.value}
      touched={fieldRenderProps.touched}
      id={fieldRenderProps.id}
      valid={fieldRenderProps.valid}
      disabled={fieldRenderProps.disabled}
      hint={fieldRenderProps.hint}
      label={fieldRenderProps.label}
      wrapperStyle={fieldRenderProps.wrapperStyle}
      optional={fieldRenderProps.optional}
      modified={fieldRenderProps.modified}
      visited={fieldRenderProps.visited}
      onChange={fieldRenderProps.onChange}
      onFocus={fieldRenderProps.onFocus}
      onBlur={fieldRenderProps.onBlur}
      name={fieldRenderProps.name}
      isLoading={isLoading}
    >
      {includeTime ? (
        <DateTimePicker key={pickerKey} name={name} value={dateValue} onChange={onChange} disabled={disabled} />
      ) : (
        <DatePicker
          key={pickerKey}
          name={name}
          value={dateValue}
          onChange={onChange}
          disabled={disabled}
          popupSettings={popupSettings}
        />
      )}
    </FormFieldWrapper>
  );
};
